'use client';

import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _div, _div2;
import { useCallback, useMemo, useState } from 'react';
import Clock from '@crehana/compass.icons.outline/Clock';
import { cn } from "../utils";
import { Popper, PopperContent, PopperTrigger } from "../Popper";
import { TimePickerColumn } from "./time-picker-column";
import { calculateTotalMinutes, formatTimeDisplay, generateMinuteArray, parseTimeValue } from "./utils";

// Lowercase with dots per product design guidelines — always "a.m" / "p.m", never "AM"/"PM".
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var AMPM_OPTIONS = ['a.m', 'p.m'];

/**
 * Columns variant — renders separate scrollable columns for hour, minute, and (optionally) a.m/p.m.
 *
 * Clicking any column item immediately emits the time via `onTimeChange`.
 * Unselected columns fall back to defaults (hour: 12/00, minute: 00, period: a.m)
 * so that the first click on any column always produces a valid time.
 */
export var TimePickerColumns = function TimePickerColumns(_ref) {
  var className = _ref.className,
    value = _ref.value,
    _ref$mode = _ref.mode,
    mode = _ref$mode === void 0 ? 'a.m/p.m' : _ref$mode,
    placeholder = _ref.placeholder,
    onTimeChange = _ref.onTimeChange,
    _ref$minuteInterval = _ref.minuteInterval,
    minuteInterval = _ref$minuteInterval === void 0 ? 1 : _ref$minuteInterval,
    disabled = _ref.disabled,
    removePortal = _ref.removePortal,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 's' : _ref$size,
    error = _ref.error,
    helperText = _ref.helperText;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    open = _useState2[0],
    setOpen = _useState2[1];

  // In a.m/p.m mode, hours are 12,1,2,...,11 (12-hour clock starting at 12).
  // In 24hour mode, hours are 0-23.
  var hours = useMemo(function () {
    if (mode === 'a.m/p.m') {
      return Array.from({
        length: 12
      }, function (_, i) {
        // Start at 12 (not 0) because 12-hour clocks show 12, 1, 2, ..., 11
        var h = i === 0 ? 12 : i;
        return ('0' + h).slice(-2);
      });
    }
    return Array.from({
      length: 24
    }, function (_, i) {
      return ('0' + i).slice(-2);
    });
  }, [mode]);
  var minutes = useMemo(function () {
    return generateMinuteArray(minuteInterval);
  }, [minuteInterval]);

  // Parse the committed 24h value into hour/minute numbers for deriving display values below.
  var parsed = useMemo(function () {
    return value ? parseTimeValue(value) : undefined;
  }, [value]);

  // Convert 24h hour to 12h display hour for the column's selectedValue.
  // E.g. value="21:30" → parsed.hour=21 → 21%12=9 → "09" (displayed in column)
  var selectedHour = useMemo(function () {
    if (!parsed) {
      return '';
    }
    if (mode === 'a.m/p.m') {
      var h12 = parsed.hour % 12 || 12;
      return ('0' + h12).slice(-2);
    }
    return ('0' + parsed.hour).slice(-2);
  }, [parsed, mode]);
  var selectedMinute = useMemo(function () {
    if (!parsed) {
      return '';
    }
    return ('0' + parsed.minute).slice(-2);
  }, [parsed]);

  // Derive a.m/p.m from the 24h hour: hours 0-11 are a.m, 12-23 are p.m.
  var selectedAmPm = useMemo(function () {
    if (!parsed || mode !== 'a.m/p.m') {
      return '';
    }
    return parsed.hour >= 12 ? 'p.m' : 'a.m';
  }, [parsed, mode]);

  /**
   * Converts 12h selections back to a 24h "HH:mm" string.
   *
   * Edge cases handled:
   * - 12 p.m (noon): h=12, amPm="p.m" → h stays 12 (12 !== 12 is false, no +12)
   * - 12 a.m (midnight): h=12, amPm="a.m" → h becomes 0
   * - 3 p.m: h=3, amPm="p.m" → h becomes 15
   *
   * In 24hour mode, the hour is already in 0-23 range so no conversion needed.
   */
  var buildValue = useCallback(function (hour, minute, amPm) {
    if (!hour || !minute) {
      return;
    }
    var h = parseInt(hour);
    if (mode === 'a.m/p.m') {
      if (amPm === 'p.m' && h !== 12) {
        h += 12;
      }
      if (amPm === 'a.m' && h === 12) {
        h = 0;
      }
    }
    return "".concat(('0' + h).slice(-2), ":").concat(minute);
  }, [mode]);

  // Emit the time immediately when any column item is clicked.
  // Fills in defaults for columns the user hasn't selected yet.
  var emitTime = useCallback(function (hour, minute, amPm) {
    var resolvedHour = hour || (mode === 'a.m/p.m' ? '12' : '00');
    var resolvedMinute = minute || '00';
    var resolvedAmPm = amPm || (mode === 'a.m/p.m' ? 'a.m' : '');
    var newValue = buildValue(resolvedHour, resolvedMinute, resolvedAmPm);
    if (newValue) {
      var _newValue$split$map = newValue.split(':').map(Number),
        _newValue$split$map2 = _slicedToArray(_newValue$split$map, 2),
        h = _newValue$split$map2[0],
        m = _newValue$split$map2[1];
      onTimeChange === null || onTimeChange === void 0 ? void 0 : onTimeChange(newValue, calculateTotalMinutes(h, m));
    }
  }, [mode, buildValue, onTimeChange]);
  var handleSelectHour = useCallback(function (hour) {
    emitTime(hour, selectedMinute, selectedAmPm);
  }, [emitTime, selectedMinute, selectedAmPm]);
  var handleSelectMinute = useCallback(function (minute) {
    emitTime(selectedHour, minute, selectedAmPm);
  }, [emitTime, selectedHour, selectedAmPm]);
  var handleSelectAmPm = useCallback(function (amPm) {
    emitTime(selectedHour, selectedMinute, amPm);
  }, [emitTime, selectedHour, selectedMinute]);

  // Convert the committed 24h value to display format for the trigger button.
  var displayValue = useMemo(function () {
    return value ? formatTimeDisplay(value, mode) : '';
  }, [value, mode]);
  return /*#__PURE__*/_jsxs("div", {
    className: "tw-flex tw-flex-col tw-gap-0.8",
    children: [/*#__PURE__*/_jsxs(Popper, {
      open: open,
      onOpenChange: setOpen,
      children: [/*#__PURE__*/_jsx(PopperTrigger, {
        asChild: true,
        disabled: disabled,
        children: /*#__PURE__*/_jsxs("button", {
          type: "button",
          disabled: disabled,
          "aria-invalid": error || undefined,
          className: cn('tw-group tw-cursor-pointer tw-justify-between tw-input tw-flex tw-items-center',
          // Override Radix's default open/closed border colors to match the input spec
          'enabled:data-[state=closed]:tw-border-neutral-light-600 enabled:data-[state=closed]:tw-ring-neutral-light-600', size === 'm' && 'tw-h-5.6 tw-font-body3 tw-text-1.6',
          // Error border persists across all interactive states (hover, focus, open)
          error && 'tw-border-alert-400 hover:tw-border-alert-400 focus-within:tw-border-alert-400 aria-expanded:tw-border-alert-400', disabled && 'tw-opacity-50 tw-pointer-events-none', className),
          children: [/*#__PURE__*/_jsx("span", {
            className: "tw-line-clamp-1 tw-grow tw-text-left",
            children: displayValue ? displayValue : /*#__PURE__*/_jsx("span", {
              className: "tw-text-neutral-light-700",
              children: placeholder
            })
          }), /*#__PURE__*/_jsx("div", {
            className: "tw-flex tw-items-center tw-shrink-0",
            children: /*#__PURE__*/_jsx(Clock, {
              className: cn(error ? 'tw-text-alert-400' : 'tw-text-neutral-light-600'),
              size: 24
            })
          })]
        })
      }), /*#__PURE__*/_jsx(PopperContent, {
        removePortal: removePortal,
        removePadding: true,
        align: "start",
        className: "tw-overflow-hidden",
        children: /*#__PURE__*/_jsxs("div", {
          className: "tw-flex",
          children: [/*#__PURE__*/_jsx(TimePickerColumn, {
            items: hours,
            selectedValue: selectedHour,
            onSelect: handleSelectHour,
            label: "Hour"
          }), _div || (_div = /*#__PURE__*/_jsx("div", {
            className: "tw-w-px tw-bg-neutral-light-400"
          })), /*#__PURE__*/_jsx(TimePickerColumn, {
            items: minutes,
            selectedValue: selectedMinute,
            onSelect: handleSelectMinute,
            label: "Minute"
          }), mode === 'a.m/p.m' && /*#__PURE__*/_jsxs(_Fragment, {
            children: [_div2 || (_div2 = /*#__PURE__*/_jsx("div", {
              className: "tw-w-px tw-bg-neutral-light-400"
            })), /*#__PURE__*/_jsx(TimePickerColumn, {
              items: AMPM_OPTIONS,
              selectedValue: selectedAmPm,
              onSelect: handleSelectAmPm,
              label: "a.m/p.m"
            })]
          })]
        })
      })]
    }), error && helperText && /*#__PURE__*/_jsx("p", {
      className: "tw-font-caption tw-text-alert-400",
      children: helperText
    })]
  });
};